import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, ImagePlus, X, UploadCloud, Loader2, Check, RotateCw } from "lucide-react";
import { API, apiErr } from "@/lib/api";

const CONSENTS = ["Private Only", "Shared with my party", "Eligible for Public Gallery"];
const MAX_MB = 15;

// Guest photo uploader: camera capture + gallery/file picker + drag-drop, previews,
// per-file captions/consent, progress, cancel, retry. Server enforces validation & storage.
export default function PhotoUploader({ albumId, onUploaded }) {
  const [items, setItems] = useState([]); // {id,file,url,caption,consent,status,progress,error}
  const [dragging, setDragging] = useState(false);
  const camRef = useRef(null);
  const fileRef = useRef(null);

  const add = (files) => {
    const picked = Array.from(files || []);
    const mapped = picked.map((file) => {
      const tooBig = file.size > MAX_MB * 1024 * 1024;
      const notImg = !file.type.startsWith("image/");
      return {
        id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 7)}`,
        file, url: URL.createObjectURL(file), caption: "", consent: CONSENTS[0],
        status: (tooBig || notImg) ? "invalid" : "ready", progress: 0,
        error: tooBig ? `Over ${MAX_MB}MB` : notImg ? "Not an image" : "",
      };
    });
    setItems((p) => [...p, ...mapped]);
  };

  const patch = (id, upd) => setItems((p) => p.map((x) => (x.id === id ? { ...x, ...upd } : x)));
  const remove = (id) => setItems((p) => p.filter((x) => x.id !== id));

  const uploadOne = (it) => new Promise((resolve) => {
    const token = localStorage.getItem("thed_token");
    const fd = new FormData();
    fd.append("file", it.file);
    fd.append("caption", it.caption || "");
    fd.append("consent", it.consent);
    const xhr = new XMLHttpRequest();
    patch(it.id, { xhr, status: "uploading", progress: 0, error: "" });
    xhr.open("POST", `${API}/portal/albums/${albumId}/upload`);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) patch(it.id, { progress: Math.round((e.loaded / e.total) * 100) }); };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) { patch(it.id, { status: "done", progress: 100 }); resolve(true); }
      else {
        let msg = "Upload failed";
        try { msg = JSON.parse(xhr.responseText).detail || msg; } catch {}
        patch(it.id, { status: "error", error: msg }); resolve(false);
      }
    };
    xhr.onerror = () => { patch(it.id, { status: "error", error: "Network error" }); resolve(false); };
    xhr.onabort = () => { patch(it.id, { status: "ready", progress: 0 }); resolve(false); };
    xhr.send(fd);
  });

  const uploadAll = async () => {
    const pending = items.filter((x) => ["ready", "error"].includes(x.status));
    if (pending.length === 0) { toast.error("Add valid photos first."); return; }
    let ok = 0;
    for (const it of pending) { if (await uploadOne(it)) ok++; }
    if (ok) { toast.success(`Uploaded ${ok} photo${ok > 1 ? "s" : ""}.`); onUploaded && onUploaded(); }
    if (ok < pending.length) toast.error("Some photos could not be uploaded. Review and retry.");
  };

  const cancel = (it) => { it.xhr && it.xhr.abort(); };

  return (
    <div data-testid="photo-uploader" className="space-y-4">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); add(e.dataTransfer.files); }}
        className={`rounded-xl border-2 border-dashed p-6 text-center transition-colors ${dragging ? "border-gold bg-gold/5" : "border-white/15"}`}
      >
        <UploadCloud className="mx-auto text-gold/70 mb-2" size={28} />
        <p className="text-sm text-muted-foreground">Drag & drop photos here, or</p>
        <div className="flex flex-wrap justify-center gap-3 mt-3">
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="pick-files" className="btn-ghost-gold px-4 py-2 text-sm flex items-center gap-2"><ImagePlus size={15} /> Choose from device</button>
          <button type="button" onClick={() => camRef.current?.click()} data-testid="open-camera" className="btn-ghost-gold px-4 py-2 text-sm flex items-center gap-2"><Camera size={15} /> Use camera</button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => add(e.target.files)} />
        <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => add(e.target.files)} />
        <p className="text-[11px] text-muted-foreground/70 mt-3">Images only · up to {MAX_MB}MB each. You confirm before anything uploads.</p>
      </div>

      {items.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((it) => (
            <div key={it.id} className="glass rounded-lg p-3 flex gap-3" data-testid={`upload-item-${it.status}`}>
              <img src={it.url} alt={it.caption || "Selected photo preview"} className="h-20 w-20 object-cover rounded-md shrink-0" />
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs truncate text-muted-foreground">{it.file.name}</p>
                  <button onClick={() => (it.status === "uploading" ? cancel(it) : remove(it.id))} aria-label="Remove photo" className="text-muted-foreground hover:text-red-300"><X size={15} /></button>
                </div>
                {it.status === "invalid" || it.status === "error" ? (
                  <p className="text-[11px] text-red-300">{it.error}</p>
                ) : (
                  <>
                    <input value={it.caption} onChange={(e) => patch(it.id, { caption: e.target.value })} placeholder="Caption (optional)" className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-xs focus:border-gold focus:outline-none" />
                    <select value={it.consent} onChange={(e) => patch(it.id, { consent: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-xs focus:border-gold focus:outline-none">
                      {CONSENTS.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </>
                )}
                {it.status === "uploading" && (
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-pool transition-all" style={{ width: `${it.progress}%` }} /></div>
                )}
                <div className="text-[11px] flex items-center gap-1">
                  {it.status === "done" && <span className="text-pool flex items-center gap-1"><Check size={12} /> Uploaded</span>}
                  {it.status === "error" && <button onClick={() => uploadOne(it)} className="text-gold flex items-center gap-1"><RotateCw size={12} /> Retry</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {items.some((x) => ["ready", "error"].includes(x.status)) && (
        <button onClick={uploadAll} data-testid="confirm-upload" className="btn-gold px-6 py-2.5 text-sm flex items-center gap-2">
          <UploadCloud size={16} /> Upload selected photos
        </button>
      )}
    </div>
  );
}
